import { Component, computed, inject, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromGenerated from '../../_generated';
import * as fromHelper from '../../analytics/analytics.helper';
import { AnalyticsService } from '../../analytics/analytics.service';
import * as fromAuth from '../../auth';
import * as fromFavorites from '../../favorites';
// Imported BY PATH, never through `../assistants`. That barrel re-exports
// `assistants.page`, which reaches `auth` -> `chats` -> the navigation
// components -> `chats/teammates` and back here. The cycle predates SB-3650,
// but pulling the barrel from a card the Teammate page renders makes it fatal:
// this component resolves to `undefined` in the page's `imports` array and
// Angular throws NG2012, which reads like anything but an import cycle.
import { AssistantLogoComponent } from '../assistants/assistant-logo.component';
import { resolveSkillCardEditCapability } from '../assistants/assistants.helper';
import {
  AssistantBuilderModalActions,
  KnowledgeModalActions,
} from '../assistants/store/assistants.actions';
import { AssistantsState } from '../assistants/store/assistants.reducer';
import { RouterService } from '../shared/services/router.service';

@Component({
  imports: [
    RouterModule,
    TranslateModule,
    AssistantLogoComponent,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.UseArrowIconDirective,
    MatTooltipModule,
  ],
  selector: 'squadbox-teammate-assistant-card',
  template: `
    <div
      role="button"
      tabindex="0"
      class="
        group
        bg-shades-white border-neutral-200 border rounded-xl
        h-full p-6 flex-1
        flex flex-col gap-4
        cursor-pointer tablet-landscape:hover:shadow-md transition-shadow duration-200
      "
      (click)="handleClickCardCTA(cardData().uuid)"
      (keydown.enter)="handleClickCardCTA(cardData().uuid)"
      (keydown.space)="
        handleClickCardCTA(cardData().uuid); $event.preventDefault()
      "
    >
      <!--header-->
      <div class="flex justify-between">
        <div class="bg-primary-50 rounded-lg size-12 p-3">
          <squadbox-assistant-logo
            class="
              [&>svg,img]:text-primary-300
              [&>svg,img]:size-6
              [&>svg,img]:shrink-0
            "
            [logo]="cardData().logo"
          />
        </div>

        <!-- Actions, measured off the Figma frame for this card
             (SquadBox - Prototypes, nodes 27019:55616 and 25309:56398): the
             Actions box is 68x32 with Edit 32x32 at x=0 and Favorite 32x32 at
             x=36, so both controls are 32px and 4px apart, top-aligned in the
             48px header and flush with its right edge. ONE flex child of the
             justify-between row, never two siblings — a third sibling would let
             the row redistribute all three. -->
        <div class="flex items-start gap-1">
          @if (status() !== 'failed' && !isMobile()) {
            @if (editAffordance().capability !== 'none') {
              <button
                [matTooltip]="editAffordance().tooltipKey | translate"
                [attr.aria-label]="editAffordance().tooltipKey | translate"
                matTooltipPosition="above"
                [matTooltipDisabled]="status() !== 'ready'"
                sdIconButton
                color="neutral"
                variant="icon"
                size="xs"
                [disabled]="status() === 'processing'"
                (click)="handleEditCTA($event)"
                (keydown.enter)="swallowKey($event)"
                (keydown.space)="swallowKey($event)"
              >
                <svg sdUseUIBasicIcon="edit" class="text-neutral-400"></svg>
              </button>
            }
          }
          <!-- Promoted from a bare 20px button to the 32x32 icon button the
               Figma frame specifies, so it matches the pencil beside it. Its
               glyph therefore moves 6px in from the card's edge. That is the
               design, and it supersedes B2's "the star occupies the same pixel
               position" criterion — which was written to stop the PENCIL
               pushing the star, not to freeze a box the design never had.
               swallowKey for the same reason as the pencil: this card is a
               role="button" that navigates to the chat on Enter, and without it
               a keyboard favourite also opens a conversation. -->
          <button
            [attr.aria-label]="
              (isFavorite()
                ? 'ASSISTANTS.DROPDOWN_MENU.REMOVE_FROM_FAVORITES'
                : 'ASSISTANTS.DROPDOWN_MENU.ADD_TO_FAVORITES'
              ) | translate
            "
            sdIconButton
            color="neutral"
            variant="icon"
            size="xs"
            (click)="handleClickToggleFavoriteCTA($event)"
            (keydown.enter)="swallowKey($event)"
            (keydown.space)="swallowKey($event)"
          >
            @if (isFavorite()) {
              <svg
                sdUseUIBasicIcon="star-filled"
                class="text-primary-500"
              ></svg>
            } @else {
              <svg sdUseUIBasicIcon="star" class="text-neutral-400"></svg>
            }
          </button>
        </div>
      </div>
      <!--content-->
      <div class="grow flex flex-col gap-3">
        <div class="flex items-center gap-2">
          <p class="typo-p1 line-clamp-2 wrap-anywhere">
            {{ cardData().name }}
          </p>
          @if (cardData().isDefaultChat) {
            <!-- [unverified: match Figma] reuses the Admin Skills page badge -->
            <span
              class="flex items-center h-6 px-3 rounded-full bg-primary-100 typo-caption leading-4 font-semibold text-primary-300 whitespace-nowrap w-fit"
            >
              {{ 'ASSISTANTS.CARD.DEFAULT_TAG' | translate }}
            </span>
          }
        </div>
        <p class="typo-p3 text-neutral-500 h-[4lh] line-clamp-4 wrap-anywhere">
          {{ cardData().description }}
        </p>
      </div>
      <!--footer-->
      <div
        class="
          flex items-end justify-between gap-4 w-full
        "
      >
        @if (shouldShowOwnerLabel()) {
          <p
            class="typo-caption font-semibold bg-primary-50 px-2 py-1 rounded w-fit text-primary-700"
          >
            @if (isSelfOwnedNonSquadBoxAssistant()) {
              {{ 'ASSISTANTS.CARD.OWNER_LABEL_ME' | translate }}
            } @else {
              {{
                'ASSISTANTS.CARD.OWNER_LABEL'
                  | translate: { ownerLabel: cardData().ownerLabel }
              }}
            }
          </p>
        }
        <div class="card-arrow-action ml-auto">
          <svg sdUseArrowIcon="arrow-right"></svg>
        </div>
      </div>
    </div>
  `,
})
export class TeammateAssistantComponent {
  private readonly routerService = inject(RouterService);
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly favoritesStore = inject(
    Store<fromFavorites.AssistantFavoritesState>,
  );
  private readonly analyticsService = inject(AnalyticsService);
  private readonly assistantsStore = inject<Store<AssistantsState>>(Store);

  public readonly cardData = input.required<fromGenerated.AssistantView>();
  /**
   * Mirrors the My Skills row's rule that the pencil is hidden on mobile
   * (`assistant-card.component.ts`). Fed from the page, which already holds the
   * layout signal, so this card takes no second store read.
   */
  public readonly isMobile = input.required<boolean>();
  protected readonly status = computed(() => this.cardData().status);
  private readonly currentUser = this.authStore.selectSignal(
    fromAuth.authFeature.selectUser,
  );
  public readonly isSelfOwnedNonSquadBoxAssistant = computed(() => {
    const ownedBy = this.cardData().ownedBy;
    const currentUser = this.currentUser();

    return (
      !!currentUser &&
      ownedBy === currentUser.id &&
      currentUser.role !== 'super_admin'
    );
  });
  public readonly shouldShowOwnerLabel = computed(
    () =>
      this.isSelfOwnedNonSquadBoxAssistant() || !!this.cardData().ownerLabel,
  );

  public readonly favoriteEntities = this.favoritesStore.selectSignal(
    fromFavorites.assistantFavoritesFeature.selectEntities,
  );

  public readonly isFavorite = computed(
    () => this.cardData().uuid in this.favoriteEntities(),
  );

  private readonly canManageKnowledgeByRole = this.authStore.selectSignal(
    fromAuth.authFeature.selectCanManageKnowledgeByRole,
  );
  private readonly isSuperAdmin = this.authStore.selectSignal(
    fromAuth.authFeature.selectIsSuperAdmin,
  );

  /**
   * Which pencil, if any, this viewer gets, and what it says
   * (SB-3650 / KD-2, SB-3650 / KD-17). The whole conjunction lives in the
   * shared resolver — do NOT restate any conjunct here, and do not relax the
   * ownership or editable-document conjuncts on the belief that the server
   * re-checks them: it mirrors the role and the trait, not those two.
   *
   * Resolved in the card rather than in the page because this card is smart and
   * already holds the whole `AssistantView`. The My Skills page computes it
   * per row only because its card is dumb.
   *
   * PHASE 2 REWRITES THE THIRD AND FOURTH ARGUMENTS. Per-skill grants retire
   * `selectCanManageKnowledgeByRole` (`auth.reducer.ts`), so it becomes a
   * per-skill capability and the `super_admin` route flag becomes per-skill too.
   * This card is the SECOND consumer of `resolveSkillCardEditCapability`;
   * `assistants.page.ts` is the first. The compiler will not catch that change —
   * grep PHASE 2.
   */
  protected readonly editAffordance = computed(() =>
    resolveSkillCardEditCapability(
      this.cardData(),
      this.currentUser()?.id,
      this.canManageKnowledgeByRole(),
      this.isSuperAdmin(),
    ),
  );

  /**
   * The pencil's two destinations. Every arm is explicit and there is NO
   * `default`: a future fourth capability must fail to compile rather than fall
   * through to the most-privileged branch. An earlier draft of the My Skills
   * card had a default arm and the security persona called it a fail-open.
   *
   * `stopPropagation` is required because this whole card is a `role="button"`
   * that navigates to the chat.
   */
  protected handleEditCTA(event: Event): void {
    event.stopPropagation();
    const assistant = this.cardData();
    const editAffordance = this.editAffordance();
    switch (editAffordance.capability) {
      case 'knowledge':
        this.assistantsStore.dispatch(
          KnowledgeModalActions.open({ assistant }),
        );
        break;
      case 'builder':
        if (editAffordance.builderRoute === 'admin') {
          this.assistantsStore.dispatch(
            AssistantBuilderModalActions.openManagedEdit({
              uuid: assistant.uuid,
            }),
          );
          break;
        }
        this.assistantsStore.dispatch(
          AssistantBuilderModalActions.openEdit({ assistant }),
        );
        break;
      case 'none':
        break;
    }
  }

  /**
   * Keyboard activation of the pencil: stop the key reaching the card's own
   * Enter/Space handlers, and let the native button's synthesized click run the
   * action ONCE. Binding `handleEditCTA` to keydown as well would dispatch
   * twice. Mirrors `armChatDropdown` in
   * `chats/components/navigation/navigation-chats-list.smart.component.ts`.
   */
  protected swallowKey(event: Event): void {
    event.stopPropagation();
  }

  public async handleClickCardCTA(uuid: string) {
    this.analyticsService
      .sendSkillOpenedEvent(
        fromHelper.toSkillOpenedEvent(this.cardData(), 'teammate_page'),
      )
      .subscribe();
    await this.routerService.navigateToNewChatWithSkill(uuid);
  }

  public handleClickToggleFavoriteCTA(event: MouseEvent) {
    event.stopPropagation();

    const assistantId = this.cardData().uuid;
    const assistant = this.cardData();
    if (this.isFavorite()) {
      this.favoritesStore.dispatch(
        fromFavorites.AssistantFavoritesActions.removeFavorite({
          assistantId,
          assistant,
        }),
      );
    } else {
      this.favoritesStore.dispatch(
        fromFavorites.AssistantFavoritesActions.addFavorite({
          assistantId,
          assistant,
        }),
      );
    }
  }
}
